import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import BackgroundIcons from '../components/BackgroundIcons';
import NotificationModal from '../components/NotificationModal';
import { apiService } from '../services/apiService';
import { generateTimelinePDF } from '../utils/pdfGenerator';

const timelineIcons = [
  { emoji: '📅', size: '6xl', opacity: '15' },
  { emoji: '⏰', size: '4xl', opacity: '15' },
  { emoji: '🕐', size: '5xl', opacity: '15' },
  { emoji: '✨', size: '3xl', opacity: '15' },
  { emoji: '📋', size: '7xl', opacity: '15' },
  { emoji: '💫', size: '4xl', opacity: '15' }
];

const TimelineDashboard = () => {
  const navigate = useNavigate();
  const [timelines, setTimelines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [isVisible, setIsVisible] = useState(false);
  const [notification, setNotification] = useState({
    show: false,
    title: '',
    message: '',
    type: 'info'
  });
  const [selectedTimeline, setSelectedTimeline] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState({ show: false, timeline: null });

  useEffect(() => {
    loadTimelines();
    setIsVisible(true);
  }, []);

  const loadTimelines = async () => {
    try {
      const response = await apiService.userTimelines.getAll();
      console.log('Loaded timelines:', response.data);
      console.log('Timeline IDs:', response.data.timelines?.map(t => ({ id: t.id, name: t.timeline_name })));
      setTimelines(response.data.timelines || []);
    } catch (error) {
      console.error('Error loading timelines:', error);
      setNotification({
        show: true,
        title: 'Error',
        message: 'Failed to load your timelines. Please try again.',
        type: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  // Calculate stats
  const stats = {
    total: timelines.length,
    favorites: timelines.filter(t => t.is_favorite).length,
    upcoming: timelines.filter(t => t.wedding_date && new Date(t.wedding_date) > new Date()).length,
    recent: timelines.filter(t => {
      const weekAgo = new Date();
      weekAgo.setDate(weekAgo.getDate() - 7);
      return new Date(t.created_at) > weekAgo;
    }).length
  };

  // Filter timelines
  const filteredTimelines = timelines.filter(timeline => {
    // Filter by favorites
    if (filter === 'favorites' && !timeline.is_favorite) return false;
    
    // Filter by upcoming/past
    if (filter === 'upcoming') {
      if (!timeline.wedding_date || new Date(timeline.wedding_date) <= new Date()) return false;
    } else if (filter === 'past') {
      if (!timeline.wedding_date || new Date(timeline.wedding_date) > new Date()) return false;
    }
    
    // Filter by search
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return (
        timeline.timeline_name.toLowerCase().includes(query) ||
        (timeline.wedding_date && timeline.wedding_date.includes(query))
      );
    }
    
    return true;
  });

  const handleToggleFavorite = async (timelineId) => {
    try {
      console.log('Toggling favorite for timeline ID:', timelineId);
      await apiService.userTimelines.toggleFavorite(timelineId);
      setTimelines(timelines.map(t => 
        t.id === timelineId ? { ...t, is_favorite: !t.is_favorite } : t
      ));
      if (selectedTimeline?.id === timelineId) {
        setSelectedTimeline({ ...selectedTimeline, is_favorite: !selectedTimeline.is_favorite });
      }
    } catch (error) {
      console.error('Error toggling favorite:', error);
      setNotification({
        show: true,
        title: 'Error',
        message: 'Failed to update favorite status',
        type: 'error'
      });
    }
  };

  const handleDelete = async () => {
    try {
      await apiService.userTimelines.delete(deleteConfirm.timeline.id);
      setTimelines(timelines.filter(t => t.id !== deleteConfirm.timeline.id));
      setDeleteConfirm({ show: false, timeline: null });
      setSelectedTimeline(null);
      setShowDetailsModal(false);
      setNotification({
        show: true,
        title: 'Timeline Deleted',
        message: '🗑️ Timeline has been removed',
        type: 'success'
      });
    } catch (error) {
      console.error('Error deleting timeline:', error);
      setNotification({
        show: true,
        title: 'Delete Failed',
        message: 'Failed to delete timeline. Please try again.',
        type: 'error'
      });
    }
  };

  const handleDownloadPDF = (timeline) => {
    try {
      const events = typeof timeline.timeline_events === 'string' 
        ? JSON.parse(timeline.timeline_events) 
        : timeline.timeline_events;
      
      generateTimelinePDF(events, timeline.timeline_name, timeline.wedding_date);
      
      setNotification({
        show: true,
        title: 'PDF Generated',
        message: '📥 Your timeline PDF is ready to print',
        type: 'success'
      });
    } catch (error) {
      console.error('Error generating PDF:', error);
      setNotification({
        show: true,
        title: 'PDF Failed',
        message: 'Failed to generate PDF. Please try again.',
        type: 'error'
      });
    }
  };

  const handleEdit = (timeline) => {
    navigate('/big-day-playbook/timeline-generator', { state: { timeline } });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-50 via-pink-25 to-champagne-50 relative overflow-hidden">
      <BackgroundIcons icons={timelineIcons} />
      
      <div className="container mx-auto px-4 py-8 relative z-10">
        
        {/* Beautiful Header - VowAI Style */}
        <div className="text-center mb-16 relative">
          
          {/* Back to Dashboard Button */}
          <div className="absolute top-0 left-0">
            <Link 
              to="/dashboard"
              className="group relative overflow-hidden bg-gradient-to-r from-rose-500 via-pink-500 to-gold-500 text-white py-3 px-6 rounded-2xl font-bold text-lg hover:shadow-2xl transform hover:scale-105 transition-all duration-300 animate-pulse"
            >
              <span className="mr-2 text-xl group-hover:animate-bounce">←</span>
              Back to Dashboard
              <div className="absolute inset-0 bg-gradient-to-r from-gold-400/20 to-rose-400/20 opacity-0 hover:opacity-100 transition-opacity duration-300 rounded-2xl"></div>
            </Link>
          </div>
          
          <div className={`inline-flex items-center justify-center w-24 h-24 bg-gradient-to-r from-purple-400 via-pink-400 to-gold-400 rounded-full shadow-2xl mb-8 animate-bounce transition-all duration-700 ${isVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-50'}`}>
            <span className="text-4xl">📅</span>
          </div>
          
          <h1 className={`text-6xl md:text-7xl font-serif font-bold bg-gradient-to-r from-purple-600 via-pink-600 to-gold-600 bg-clip-text text-transparent mb-6 tracking-wide transition-all duration-700 delay-100 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            Wedding Timelines
          </h1>
          
          <div className="flex items-center justify-center mb-6">
            <div className="w-32 h-1 bg-gradient-to-r from-transparent via-gold-400 to-transparent rounded-full"></div>
            <div className="mx-4 text-3xl animate-bounce">⏰</div>
            <div className="w-32 h-1 bg-gradient-to-r from-transparent via-gold-400 to-transparent rounded-full"></div>
          </div>
          
          <p className={`text-2xl text-gray-700 font-light italic transition-all duration-700 delay-300 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            Your perfectly planned wedding days ✨
          </p>
          
          <div className="mt-8">
            <span className="px-6 py-3 bg-gradient-to-r from-purple-400 to-pink-500 text-white rounded-full font-bold text-lg shadow-xl animate-pulse">
              {stats.total} Timeline{stats.total !== 1 ? 's' : ''} Created
            </span>
          </div>
        </div>

        {/* Stats Cards */}
        <div className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8 transition-all duration-700 delay-200 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="group bg-white/70 backdrop-blur-lg rounded-2xl p-6 shadow-lg border border-rose-100 hover:shadow-2xl transform hover:scale-105 transition-all duration-300">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm font-medium">Total Timelines</p>
                <p className="text-3xl font-bold bg-gradient-to-r from-rose-500 to-pink-500 bg-clip-text text-transparent">{stats.total}</p>
              </div>
              <div className="bg-rose-100 rounded-full p-3">
                <span className="text-2xl group-hover:animate-bounce">📅</span>
              </div>
            </div>
          </div>

          <div className="group bg-white/70 backdrop-blur-lg rounded-2xl p-6 shadow-lg border border-gold-100 hover:shadow-2xl transform hover:scale-105 transition-all duration-300">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm font-medium">Favorites</p>
                <p className="text-3xl font-bold bg-gradient-to-r from-gold-500 to-yellow-500 bg-clip-text text-transparent">{stats.favorites}</p>
              </div>
              <div className="bg-gold-100 rounded-full p-3">
                <span className="text-2xl group-hover:animate-bounce">⭐</span>
              </div>
            </div>
          </div>

          <div className="group bg-white/70 backdrop-blur-lg rounded-2xl p-6 shadow-lg border border-purple-100 hover:shadow-2xl transform hover:scale-105 transition-all duration-300">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm font-medium">Upcoming</p>
                <p className="text-3xl font-bold bg-gradient-to-r from-purple-500 to-pink-500 bg-clip-text text-transparent">{stats.upcoming}</p>
              </div>
              <div className="bg-purple-100 rounded-full p-3">
                <span className="text-2xl group-hover:animate-bounce">🎉</span>
              </div>
            </div>
          </div>

          <div className="group bg-white/70 backdrop-blur-lg rounded-2xl p-6 shadow-lg border border-emerald-100 hover:shadow-2xl transform hover:scale-105 transition-all duration-300">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm font-medium">Recent (7 days)</p>
                <p className="text-3xl font-bold bg-gradient-to-r from-emerald-500 to-teal-500 bg-clip-text text-transparent">{stats.recent}</p>
              </div>
              <div className="bg-emerald-100 rounded-full p-3">
                <span className="text-2xl group-hover:animate-bounce">✨</span>
              </div>
            </div>
          </div>
        </div>

        {/* Search and Filter Bar */}
        <div className={`bg-white/70 backdrop-blur-lg rounded-3xl p-6 border-2 border-rose-200/50 shadow-lg mb-8 transition-all duration-700 delay-400 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="flex flex-col md:flex-row gap-4">
            {/* Search */}
            <div className="flex-1">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search timelines..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-12 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-pink-200 focus:border-pink-400 transition-all"
                />
                <span className="absolute left-4 top-1/2 transform -translate-y-1/2 text-2xl">🔍</span>
              </div>
            </div>

            {/* Filter Buttons */}
            <div className="flex gap-2">
              <button
                onClick={() => setFilter('all')}
                className={`px-6 py-3 rounded-xl font-bold transition-all ${
                  filter === 'all'
                    ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white shadow-lg'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                All
              </button>
              <button
                onClick={() => setFilter('favorites')}
                className={`px-6 py-3 rounded-xl font-bold transition-all ${
                  filter === 'favorites'
                    ? 'bg-gradient-to-r from-gold-500 to-yellow-500 text-white shadow-lg'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                ⭐ Favorites
              </button>
              <button
                onClick={() => setFilter('upcoming')}
                className={`px-6 py-3 rounded-xl font-bold transition-all ${
                  filter === 'upcoming'
                    ? 'bg-gradient-to-r from-emerald-500 to-teal-500 text-white shadow-lg'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Upcoming
              </button>
              <button
                onClick={() => setFilter('past')}
                className={`px-6 py-3 rounded-xl font-bold transition-all ${
                  filter === 'past'
                    ? 'bg-gradient-to-r from-gray-500 to-gray-600 text-white shadow-lg'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Past
              </button>
            </div>
          </div>
        </div>

        {/* Create New Timeline Button */}
        <div className={`mb-8 transition-all duration-700 delay-500 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <Link
            to="/big-day-playbook/timeline-generator"
            className="group relative overflow-hidden block w-full bg-gradient-to-r from-rose-500 via-pink-500 to-gold-500 text-white py-5 px-8 rounded-3xl font-bold text-xl text-center hover:shadow-2xl transform hover:scale-105 transition-all duration-300 animate-pulse"
          >
            <span className="mr-3 text-2xl group-hover:animate-bounce inline-block">✨</span>
            Create New Timeline
            <div className="absolute inset-0 bg-gradient-to-r from-gold-400/20 to-rose-400/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-3xl"></div>
          </Link>
        </div>

        {/* Timelines Grid */}
        {loading ? (
          <div className="text-center py-20">
            <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-pink-600 mx-auto mb-4"></div>
            <p className="text-gray-600 text-lg">Loading your timelines...</p>
          </div>
        ) : filteredTimelines.length === 0 ? (
          <div className="text-center py-20">
            <div className="text-8xl mb-6 opacity-50">📅</div>
            <h3 className="text-2xl font-bold text-gray-700 mb-4">
              {searchQuery || filter !== 'all' ? 'No timelines found' : 'No timelines yet'}
            </h3>
            <p className="text-gray-600 mb-8">
              {searchQuery || filter !== 'all' 
                ? 'Try adjusting your search or filter' 
                : 'Create your first wedding timeline to get started'}
            </p>
            {!searchQuery && filter === 'all' && (
              <Link
                to="/big-day-playbook/timeline-generator"
                className="inline-block bg-gradient-to-r from-blue-500 to-cyan-500 text-white py-3 px-8 rounded-2xl font-bold hover:shadow-lg transform hover:scale-105 transition-all"
              >
                Create Timeline
              </Link>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredTimelines.map((timeline, index) => {
              const events = typeof timeline.timeline_events === 'string' 
                ? JSON.parse(timeline.timeline_events) 
                : (timeline.timeline_events || []);
              
              return (
                <div
                  key={timeline.id}
                  onClick={() => {
                    setSelectedTimeline(timeline);
                    setShowDetailsModal(true);
                  }}
                  className={`group relative bg-gradient-to-br from-white via-purple-50/30 to-pink-50/30 backdrop-blur-lg rounded-3xl shadow-xl border-2 border-transparent hover:border-purple-300 overflow-hidden hover:shadow-2xl transform hover:scale-105 transition-all duration-500 cursor-pointer ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
                  style={{ transitionDelay: `${600 + index * 100}ms` }}
                >
                  {/* Animated Gradient Border Effect */}
                  <div className="absolute inset-0 bg-gradient-to-r from-purple-400 via-pink-400 to-gold-400 opacity-0 group-hover:opacity-20 transition-opacity duration-500 rounded-3xl"></div>
                  
                  {/* Floating Icons */}
                  <div className="absolute top-4 right-4 text-3xl opacity-20 group-hover:opacity-40 transition-opacity duration-500 animate-pulse">⏰</div>
                  <div className="absolute bottom-4 left-4 text-2xl opacity-20 group-hover:opacity-40 transition-opacity duration-500 animate-pulse delay-100">✨</div>
                  
                  {/* Favorite Star */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleToggleFavorite(timeline.id);
                    }}
                    className="absolute top-4 left-4 z-20 text-3xl hover:scale-125 transition-transform drop-shadow-lg"
                  >
                    {timeline.is_favorite ? '⭐' : '☆'}
                  </button>
                  
                  {/* Card Content */}
                  <div className="p-6 pt-16 relative z-10">
                    {/* Shimmer Effect on Hover */}
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
                    
                    {/* Timeline Name */}
                    <h3 className="text-2xl font-serif font-bold mb-3 group-hover:scale-105 transition-transform duration-300 bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
                      {timeline.timeline_name}
                    </h3>
                    
                    {/* Wedding Date */}
                    {timeline.wedding_date && (
                      <p className="text-sm text-gray-600 flex items-center mb-2">
                        <span className="mr-2">📅</span>
                        {new Date(timeline.wedding_date).toLocaleDateString('en-US', {
                          weekday: 'short',
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric'
                        })}
                      </p>
                    )}

                    {/* Info */}
                    <div className="space-y-2 mb-4">
                      {timeline.ceremony_time && (
                        <p className="text-sm text-gray-600 flex items-center">
                          <span className="mr-2">💒</span>
                          Ceremony: {timeline.ceremony_time}
                        </p>
                      )}
                      <p className="text-sm text-gray-600 flex items-center">
                        <span className="mr-2">📋</span>
                        {events.length} events
                      </p>
                    </div>

                  {/* Action Buttons */}
                  <div className="flex gap-2 mt-4">
                    <button 
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedTimeline(timeline);
                        setShowDetailsModal(true);
                      }} 
                      className="group/viewbtn flex-1 relative overflow-hidden bg-gradient-to-r from-purple-600 to-pink-600 text-white py-2 px-4 rounded-xl font-bold text-sm hover:shadow-lg transform hover:scale-105 transition-all duration-300 animate-pulse"
                    >
                      <span className="relative z-10 flex items-center justify-center">
                        <span className="mr-1 group-hover/viewbtn:animate-bounce">✨</span>
                        View Details
                      </span>
                      <div className="absolute inset-0 bg-gradient-to-r from-pink-400/20 to-purple-400/20 opacity-0 group-hover/viewbtn:opacity-100 transition-opacity duration-300 rounded-xl"></div>
                    </button>
                    <button 
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEdit(timeline);
                      }} 
                      className="group/edit flex-1 relative overflow-hidden bg-gradient-to-r from-purple-500 via-indigo-500 to-blue-500 text-white py-2 px-4 rounded-xl font-bold text-sm hover:shadow-lg transform hover:scale-105 transition-all duration-300 animate-pulse"
                    >
                      <span className="relative z-10 flex items-center justify-center">
                        <span className="mr-1 group-hover/edit:animate-bounce">✏️</span>
                        Edit
                      </span>
                      <div className="absolute inset-0 bg-gradient-to-r from-blue-400/20 to-purple-400/20 opacity-0 group-hover/edit:opacity-100 transition-opacity duration-300 rounded-xl"></div>
                    </button>
                    <button 
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDownloadPDF(timeline);
                      }}
                      className="px-4 py-3 bg-gradient-to-r from-rose-200 to-pink-200 hover:from-rose-300 hover:to-pink-300 rounded-xl transition-all duration-300 text-xl border-2 border-rose-300 transform hover:scale-110 hover:rotate-12 shadow-md hover:shadow-lg"
                    >
                      📥
                    </button>
                    <button 
                      onClick={(e) => {
                        e.stopPropagation();
                        setDeleteConfirm({ show: true, timeline });
                      }}
                      className="px-4 py-3 bg-gradient-to-r from-red-200 to-rose-200 hover:from-red-300 hover:to-rose-300 text-red-700 rounded-xl transition-all duration-300 text-xl border-2 border-red-300 transform hover:scale-110 hover:rotate-12 shadow-md hover:shadow-lg font-bold"
                    >
                      🗑️
                    </button>
                  </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm.show && (
        <NotificationModal
          isOpen={deleteConfirm.show}
          onClose={() => setDeleteConfirm({ show: false, timeline: null })}
          title="Delete Timeline"
          message={`Are you sure you want to delete "${deleteConfirm.timeline?.timeline_name}"? This action cannot be undone.`}
          type="warning"
          isConfirmation={true}
          confirmText="Yes, Delete"
          cancelText="Cancel"
          onConfirm={handleDelete}
        />
      )}

      {/* Notification Modal */}
      <NotificationModal
        isOpen={notification.show}
        onClose={() => setNotification({ ...notification, show: false })}
        title={notification.title}
        message={notification.message}
        type={notification.type}
      actionLabel={notification.actionLabel}
      onAction={notification.onAction}
      />
      </div>

      {/* Details Modal - Outside relative container for proper z-index */}
      {showDetailsModal && selectedTimeline && (
        <div 
          className="fixed inset-0 bg-black/40 backdrop-blur-lg z-[9999] flex items-center justify-center p-4 animate-in fade-in duration-500" 
          onClick={() => setShowDetailsModal(false)}
        >
          <div 
            className="bg-gradient-to-br from-white/95 via-rose-50/30 to-champagne-50/30 backdrop-blur-xl rounded-3xl shadow-2xl border-2 border-white/20 max-w-3xl w-full max-h-[90vh] overflow-y-auto transform animate-in zoom-in slide-in-from-bottom-4 duration-700" 
            onClick={(e) => e.stopPropagation()}
          >
            {/* Floating VowAI Elements */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none rounded-3xl">
              <div className="absolute top-4 left-8 text-white/30 text-xl animate-pulse">✨</div>
              <div className="absolute top-8 right-16 text-white/40 text-lg animate-pulse" style={{animationDelay: '0.5s'}}>💫</div>
              <div className="absolute bottom-6 left-16 text-white/30 text-sm animate-pulse" style={{animationDelay: '1s'}}>⭐</div>
              <div className="absolute top-12 right-8 text-rose-300/20 text-2xl animate-bounce" style={{animationDelay: '0.3s', animationDuration: '3s'}}>💖</div>
              <div className="absolute bottom-12 left-8 text-pink-300/25 text-xl animate-bounce" style={{animationDelay: '1.5s', animationDuration: '4s'}}>💕</div>
              <div className="absolute top-16 right-20 w-16 h-16 bg-gradient-to-br from-rose-200/10 to-pink-300/15 rounded-full blur-xl animate-pulse" style={{animationDelay: '0s', animationDuration: '4s'}}></div>
              <div className="absolute bottom-20 left-12 w-20 h-20 bg-gradient-to-br from-champagne-200/10 to-gold-300/15 rounded-full blur-2xl animate-pulse" style={{animationDelay: '2s', animationDuration: '5s'}}></div>
            </div>

            {/* Header */}
            <div className="bg-gradient-to-r from-purple-500 via-pink-500 to-gold-500 p-8 rounded-t-3xl relative overflow-hidden">
              {/* Header magical elements */}
              <div className="absolute inset-0 overflow-hidden pointer-events-none">
                <div className="absolute -top-10 -right-10 w-32 h-32 bg-white/10 rounded-full blur-2xl"></div>
                <div className="absolute -bottom-10 -left-10 w-40 h-40 bg-white/5 rounded-full blur-3xl"></div>
                <div className="absolute top-4 left-8 text-white/20 text-2xl animate-pulse">✨</div>
                <div className="absolute top-8 right-16 text-white/30 text-lg animate-pulse" style={{animationDelay: '0.5s'}}>💫</div>
                <div className="absolute bottom-6 left-16 text-white/20 text-sm animate-pulse" style={{animationDelay: '1s'}}>⭐</div>
              </div>

              <button
                onClick={() => setShowDetailsModal(false)}
                className="absolute top-4 right-4 w-10 h-10 bg-white/20 rounded-full flex items-center justify-center text-white hover:bg-white/30 hover:rotate-90 transition-all duration-300 z-10"
              >
                <span className="text-xl">✕</span>
              </button>
              
              <div className="flex items-center space-x-6 relative z-10">
                <div className="w-20 h-20 bg-white/20 rounded-2xl flex items-center justify-center shadow-lg backdrop-blur-sm border border-white/30">
                  <span className="text-5xl">📅</span>
                </div>
                <div>
                  <h2 className="text-3xl font-serif font-bold text-white mb-2 drop-shadow-lg">
                    {selectedTimeline.timeline_name}
                  </h2>
                  {selectedTimeline.wedding_date && (
                    <p className="text-white/90 italic text-lg drop-shadow-md">
                      {new Date(selectedTimeline.wedding_date).toLocaleDateString('en-US', {
                        weekday: 'long',
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Modal Content */}
            <div className="p-8 relative z-10">
              <h3 className="text-2xl font-serif font-bold bg-gradient-to-r from-purple-600 via-pink-600 to-gold-600 bg-clip-text text-transparent mb-6">
                Timeline Events
              </h3>
              <div className="space-y-3">
                {(typeof selectedTimeline.timeline_events === 'string' 
                  ? JSON.parse(selectedTimeline.timeline_events) 
                  : (selectedTimeline.timeline_events || [])).map((event, index, allEvents) => {
                    // Check if we should show date label
                    const showDateLabel = index === 0 || (event.dateLabel && event.dateLabel !== allEvents[index - 1]?.dateLabel);
                    
                    return (
                      <div key={index}>
                        {/* Date Label Divider */}
                        {showDateLabel && event.dateLabel && (
                          <div className="flex items-center gap-3 my-4">
                            <div className="flex-1 h-px bg-gradient-to-r from-transparent via-gold-400 to-transparent"></div>
                            <div className="px-4 py-2 bg-gradient-to-r from-gold-500 to-yellow-500 text-white rounded-full font-bold text-sm shadow-lg">
                              📅 {event.dateLabel} {event.date && `- ${new Date(event.date + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`}
                            </div>
                            <div className="flex-1 h-px bg-gradient-to-r from-transparent via-gold-400 to-transparent"></div>
                          </div>
                        )}
                        
                        <div className="bg-gradient-to-r from-rose-50/80 via-pink-50/60 to-champagne-50/80 backdrop-blur-sm rounded-2xl p-5 border-2 border-rose-200/30 shadow-md hover:shadow-lg transition-all duration-300 hover:scale-[1.02]">
                          <div className="flex items-start gap-4">
                            <div className="w-12 h-12 bg-white/80 rounded-xl flex items-center justify-center shadow-md flex-shrink-0">
                              <span className="text-3xl">{event.icon}</span>
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center justify-between mb-2">
                                <h4 className="font-bold text-gray-800 text-lg">{event.event}</h4>
                                <span className="text-sm font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent bg-white/80 px-3 py-1 rounded-lg shadow-sm">
                                  {event.time}
                                </span>
                              </div>
                              {event.description && (
                                <p className="text-sm text-gray-600 leading-relaxed">{event.description}</p>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TimelineDashboard;
